import axios from 'axios';

// Store
import store from '../store/store';

// Configs
import { SURVEY_RASTER_SERVICE } from '../configs/map.configs';
import {
  NO_IMPROVED_WATER_PREMISES_FIELD_VALUE,
  NO_IMPROVED_WATER_PREMISES_FIELD_VALUE_BACKUP,
} from '../configs/indicators.config';
import { RASTER_FIELD_FOR_POPULATION, RASTER_FILED_FOR_UNCERTAINTY } from '../configs/attributeFields.configs';

export type AllSurveysRasterServiceType = {
  name: string;
  type: string;
  url: string;
};

export type AllSurveysRasterServicesObjectType = {
  [key in string]: AllSurveysRasterServiceType;
};

/**
 * Get Survey Raster Service REST endpoint data
 * @param {string} url - REST endpoint URL
 * @returns {Promise<any>} - REST endpoint data
 */
export const getRestEndpointData = async (url: string): Promise<any> => {
  const results = await axios({
    method: 'GET',
    url: url,
    params: {
      f: 'json',
    },
  }).catch((error) => {
    console.error(error);
  });

  return results;
};

/**
 * Get all Survey Raster Services REST endpoint data
 * @returns {Promise<AllSurveysRasterServicesObjectType>} - All Survey Raster Services REST endpoint data
 */
export const getAllSurveysRasterServicesData = async () => {
  const results = await getRestEndpointData(SURVEY_RASTER_SERVICE);

  const allSurveysRasterServicesObject: AllSurveysRasterServicesObjectType = {};

  results?.data.services
    .filter((service: any) => {
      return service.name.includes('Admin2SDR_rasters_');
    })
    .map((service: any) => {
      const dhscc = service.name.slice(-2);

      const admin2RasterPathChunk = service.name.split('/')[1];
      const admin2RasterURl = SURVEY_RASTER_SERVICE + '/' + admin2RasterPathChunk + '/' + service.type;
      const servicesObject: AllSurveysRasterServiceType = {
        name: service.name,
        type: service.type,
        url: admin2RasterURl,
      };

      allSurveysRasterServicesObject[dhscc] = servicesObject;
    });

  return allSurveysRasterServicesObject;
};

/**
 * Get the sublayers for the 5x5 km layer
 * @param {__esri.MapImageLayer} countrySurveySubLayers - The map image layer
 * @returns {Promise<any>} - The sublayers for the 5x5 km layer
 */
export const getSelectedSurveySubLayers = (countrySurveySubLayers: any): [] => {
  const { indicatorSelected, surveySelected } = store.getState().admin2EstimatesSlice;
  const indicatorId = indicatorSelected?.value;
  const surveyYear = surveySelected?.year;

  const selectedSurveySubLayers = countrySurveySubLayers.filter((layer: any) => {
    // TODO: This is a temporary fix to handle the fact that the indicator value is not the same as the field name in the GIS REST API
    if (indicatorId === NO_IMPROVED_WATER_PREMISES_FIELD_VALUE_BACKUP) {
      return layer.name_clean.includes(NO_IMPROVED_WATER_PREMISES_FIELD_VALUE) && layer.name_clean.includes(surveyYear);
    } else {
      return layer.name_clean.includes(indicatorId) && layer.name_clean.includes(surveyYear);
    }
  });

  return selectedSurveySubLayers;
};

export type SubLayerProcessedType = {
  id: number;
  name: string;
  name_clean: string;
  raster_type: string;
};

/**
 * Get the sublayers for the 5x5 km layer
 * @param {__esri.MapImageLayer} layer - The map image layer
 * @returns {Promise} - The sublayers for the 5x5 km layer
 */
export const get_5x5_SurveySubLayers = async (layer: __esri.MapImageLayer): Promise<any> => {
  const resultsRaster = await getRestEndpointData(layer.url);
  const { layers, mapName } = resultsRaster.data;
  const subLayersProcessed: SubLayerProcessedType[] = layers.map((layer: any) => {
    const layerNameClean = layer.name.split('.')[0];

    let rasterType: any;
    if (layerNameClean.endsWith(RASTER_FIELD_FOR_POPULATION)) {
      rasterType = RASTER_FIELD_FOR_POPULATION;
    } else if (layerNameClean.endsWith(RASTER_FILED_FOR_UNCERTAINTY)) {
      rasterType = RASTER_FILED_FOR_UNCERTAINTY;
    } else {
      rasterType = '';
    }

    return {
      id: layer.id,
      name: layer.name,
      name_clean: layerNameClean,
      raster_type: rasterType,
    };
  });

  const countriesSurveySubLayers = {
    [mapName]: subLayersProcessed,
  };

  const selectedSurveySubLayers = getSelectedSurveySubLayers(subLayersProcessed);

  return { countriesSurveySubLayers, selectedSurveySubLayers };
};
